import { Stack, useBoolean } from '@chakra-ui/react';
import { RootState, store } from 'modules/Editor/store';
import {
  selectIsAccessibilityView,
  selectIsSlideView,
  selectIsWidgetSettingsView,
} from 'modules/Editor/store/editorSettingsSelector';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import { ACCESSIBILITY_MENU_WIDTH } from '../AccessibilityManager/AccessibilityManager.config';
import { SCROLL_CONTAINER_ID } from '../PageArea/PageArea.config';
import { SLIDE_WIDTH } from '../PageManager/Slides/Slide.config';
import {
  CheckContextMenuItemFnArgs,
  ContextMenuItem as ContextMenuItemI,
  OnContextMenuItemClickFn,
} from './ContextMenu.types';
import { ContextMenuItem } from './ContextMenuItem';

/**
 * CONTEXT MENU LIST COMPONENT
 *
 * Container for context menu items
 */
interface ContextMenuListProps {
  contextMenuItems: ContextMenuItemI[];
  contextMenuData?: CheckContextMenuItemFnArgs;
  onClickWrapper?: (e: MouseEvent<HTMLButtonElement>, callback: (args: OnContextMenuItemClickFn) => void) => void;
  position?: { x: number; y: number };
}

const WIDTH_THRESHOLD = 70;
const HEIGHT_THRESHOLD = 10;

export const ContextMenuList = ({
  contextMenuItems,
  onClickWrapper,
  contextMenuData,
  position = { x: 0, y: 0 },
}: ContextMenuListProps) => {
  const listRef = useRef<HTMLDivElement | null>(null);

  const [showMenu, setShowMenu] = useBoolean(false);
  const [translate, setTranslate] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Compute translate on mount so that entire list remains visible on screen
  // Show menu once the translate has been computed so there is no glitch/jump
  useEffect(() => {
    if (!listRef.current) return;

    const storeState = store.getState() as RootState;
    const isSlideView = selectIsSlideView(storeState);
    const isA11yOpen = selectIsAccessibilityView(storeState);
    const isRightSettingsOpen = selectIsWidgetSettingsView(storeState);

    const menuHeight = listRef.current.clientHeight;
    const menuWidth = listRef.current.clientWidth;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    if (!menuHeight || !menuWidth) {
      setShowMenu.on();
      return;
    }

    const pageScrollContainer = document.getElementById(SCROLL_CONTAINER_ID);
    const targetPageRect = pageScrollContainer?.getBoundingClientRect() ?? { x: 0, y: 0 };
    const scrollTop = pageScrollContainer?.scrollTop ?? 0;
    const scrollLeft = pageScrollContainer?.scrollLeft ?? 0;

    let { x, y } = position;
    let newTranslate = { x: 0, y: 0 };

    // Convert position to page coordinates
    x = x + targetPageRect.x - scrollLeft;
    y = y + targetPageRect.y - scrollTop;

    // Compute the visible width depending on whether any right panels are open
    let visiblePageAreaWidth = windowWidth - WIDTH_THRESHOLD;
    if (isSlideView) {
      visiblePageAreaWidth -= SLIDE_WIDTH;
    } else if (isA11yOpen || isRightSettingsOpen) {
      visiblePageAreaWidth -= ACCESSIBILITY_MENU_WIDTH;
    }

    // Overflows right edge
    // Menu should appear on left
    if (x + menuWidth > visiblePageAreaWidth) {
      newTranslate.x = -menuWidth;
    }

    // Overflows bottom of screen
    // Shift so that last menu item touches bottom of screen
    if (y + menuHeight > windowHeight - HEIGHT_THRESHOLD) {
      newTranslate.y = windowHeight - (y + menuHeight) - HEIGHT_THRESHOLD;
    }

    setTranslate(newTranslate);
    setShowMenu.on();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack
      position={showMenu ? 'absolute' : 'fixed'}
      data-testid='context-menu-list'
      bg='white'
      spacing={0}
      borderRadius='base'
      w='fit-content'
      boxShadow='0px 0px 4px rgba(0, 0, 0, 0.24)'
      visibility={showMenu ? 'visible' : 'hidden'}
      ref={listRef}
      transform={`translate(${translate.x}px, ${translate.y}px)`}
    >
      {contextMenuItems.map((item) => (
        <ContextMenuItem
          key={item.id}
          menuItemData={item}
          contextMenuData={contextMenuData}
          onClickWrapper={onClickWrapper}
        />
      ))}
    </Stack>
  );
};
